import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    CATEGORY_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { HistoryDetailsEntityCell } from "../../cells/HistoryDetailsEntityCell";
import { HistoryDetailsAmountCell } from "../../cells/HistoryDetailsAmountCell";
import { SplitExpandCell } from "../cells/SplitExpandCell";
import { SplitCategoryCell } from "../cells/SplitCategoryCell";
import { SplitLedgerCell } from "../cells/SplitLedgerCell";
import { SplitCountCell } from "../cells/SplitCountCell";
import { SplitVersionDateCell } from "../cells/SplitVersionDateCell";
import { SplitDescriptionCell } from "../cells/SplitDescriptionCell";
import { SplitLedgerInfo } from "../schema";

export interface SplitVersionRow extends HistoryDetailsBaseRow {
    id: string;
    batchKey: string;
    splitsCount: number;
    bankDescription: string;
    category: Partial<Category>;
    ledger: SplitLedgerInfo;
    amount: number;
    originalAmount: number;
    entity: Partial<Entity>;
}

export enum SplitVersionColumn {
    DATE = "date",
    SPLITS = "splitsCount",
    DESCRIPTION = "bankDescription",
    CATEGORY = "category",
    LEDGER = "ledger",
    AMOUNT = "amount",
    ENTITY = "entity",
    ACTIONS = "actions",
}

type SplitVersionsViewConfig = ViewConfig<SplitVersionRow, string, never>;

const CATEGORY_CLASSIFICATION_COLUMN: SplitVersionsViewConfig["columns"] = {
    [SplitVersionColumn.CATEGORY]: {
        accessorKey: "category",
        name: "Category",
        header: "Category",
        cell: SplitCategoryCell,
        ...CATEGORY_COLUMN_SIZE,
    },
};

const LEDGER_CLASSIFICATION_COLUMN: SplitVersionsViewConfig["columns"] = {
    [SplitVersionColumn.LEDGER]: {
        accessorKey: "ledger",
        name: "Cash ledger",
        header: "Cash ledger",
        cell: SplitLedgerCell,
        ...CATEGORY_COLUMN_SIZE,
    },
};

export const createSplitVersionsViewConfig = (
    isGlFirst: boolean,
): SplitVersionsViewConfig => ({
    columns: {
        [SplitVersionColumn.DATE]: {
            accessorKey: "date",
            name: "Time",
            header: "",
            cell: SplitVersionDateCell,
            immutable: true,
            ...DATE_COLUMN_SIZE,
        },
        [SplitVersionColumn.SPLITS]: {
            accessorKey: "splitsCount",
            name: "Splits",
            header: "Splits",
            cell: SplitCountCell,
            minSize: 60,
            maxSize: 80,
        },
        [SplitVersionColumn.DESCRIPTION]: {
            accessorKey: "bankDescription",
            name: "Description",
            header: "Description",
            cell: SplitDescriptionCell,
            minSize: 200,
            maxSize: 600,
        },
        ...(isGlFirst
            ? LEDGER_CLASSIFICATION_COLUMN
            : CATEGORY_CLASSIFICATION_COLUMN),
        [SplitVersionColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: HistoryDetailsAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [SplitVersionColumn.ENTITY]: {
            accessorKey: "entity",
            name: "Entity",
            header: "Entity",
            cell: HistoryDetailsEntityCell,
            minSize: 56,
            maxSize: 150,
        },
        [SplitVersionColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: SplitExpandCell,
            minSize: 56,
            maxSize: 56,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
});
